import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { PauseIcon } from "@images/svgIcons/pauseIcon"
import { PlayIcon } from "@images/svgIcons/playIcon"
import { ResumeIcon } from "@images/svgIcons/resumeIcon"
import { TrophyIcon } from "@images/svgIcons/trophyIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression, useProgramOnOffProgressionActions } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { ProgramStatus, ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { Circles } from "@modules/ui/components/circles"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { useMemo } from "react"

type progressionRateProps = {
  programId: string
  editionAllowed: boolean
  className?: string
} & React.ComponentProps<"div">

export function ProgressionRate({ programId, editionAllowed, className }: progressionRateProps) {
  const confirmPopup = useConfirmPopup()
  const { data: program } = useProgram(programId)
  const { data: progression } = useProgramOnOffProgression(
    programId,
    program.type !== ProgramType.ONOFF ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )
  const { isUpdating, startProgram, stopProgram, restartProgram } = useProgramOnOffProgressionActions(program.id)

  const weekNumber = Math.ceil(program.duration ?? 0 / 7)
  const fillRatePoints = useMemo(() => {
    if (!progression || progression.status === ProgramProgressionStatus.NOT_STARTED) return 0

    const fillRate =
      !progression.currentIndex || !progression.duration
        ? 0
        : ((progression.currentIndex + 1) / progression.duration) * 100
    const fillRatePointsValue = Math.round(fillRate / 10)

    return Math.min(10, Math.max(0, fillRatePointsValue))
  }, [progression])

  const isPayed =
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
  const isPaused = progression?.status === ProgramProgressionStatus.STOPPED
  const isNotStarted = progression?.status === ProgramProgressionStatus.NOT_STARTED
  const isStarted = progression?.status === ProgramProgressionStatus.STARTED
  const isCompleted = progression?.status === ProgramProgressionStatus.COMPLETED
  const mainTextColor = isCompleted ? "text-textOnColor-heading" : isPaused ? "text-neutral-600" : "text-neutral-900"
  const secondaryTextColor = isCompleted ? "text-secondary-200" : isPaused ? "text-neutral-600" : "text-neutral-700"

  const handleStartProgram = async () => {
    await startProgram().catch((e) => {
      console.error(LoggerType.Program + "startProgram", e)
      toast.error(word("program.details.start.error.toast"), { classNames: errorToastClassName() })
    })
  }

  const handleStopProgram = async () => {
    await stopProgram().catch((e) => {
      console.error(LoggerType.Program + "stopProgram", e)
      toast.error(word("program.details.pause.error.toast"), { classNames: errorToastClassName() })
    })
  }

  const handleRestartProgram = () => {
    confirmPopup.show({
      title: word("program.restart.warning.title"),
      message: word("program.restart.warning.description"),
      accept: async () => {
        await restartProgram()
          .then(() => {
            toast.success(word("program.restart.success.toast"), { classNames: successToastClassName() })
          })
          .catch((e) => {
            console.error(LoggerType.Program + "restartProgram", e)
            toast.error(word("program.details.restart.error.toast"), { classNames: errorToastClassName() })
          })
      },
    })
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Text fontStyle="LS" className="text-textDefault-caption">
        {word("program.onOff.formik.label").toUpperCase()}
      </Text>

      <div
        className={cn(
          "mb-5 flex flex-col gap-2 rounded-4xl p-6",
          isCompleted
            ? "bg-linear-to-r from-secondary-800 to-secondary-600"
            : isPaused
              ? "bg-neutral-300"
              : "bg-neutral-100",
        )}
      >
        <div className="mb-2 flex items-center">
          <Text fontStyle="TL" className={`mr-2 ${mainTextColor}`}>
            {`${program.duration}`} {word("global.day.label", { count: program.duration ?? 0 })}
          </Text>
          <Text
            fontStyle="BM"
            className={secondaryTextColor}
          >{`( ${weekNumber} ${word("global.week.label", { count: weekNumber })} )`}</Text>
        </div>
        {isPayed ? (
          <>
            <div className="mb-3 flex justify-between">
              <div className="flex w-1/2 flex-col items-center">
                <Text fontStyle="LS" className={secondaryTextColor}>
                  {word("program.details.progression.progress")}
                </Text>
                {isCompleted ? (
                  <div className="mb-1 flex flex-row items-center gap-1">
                    <TrophyIcon className={mainTextColor} color="currentColor" />
                    <Text fontStyle="BM" className={mainTextColor}>
                      {word("program.details.progression.progress_completed")}
                    </Text>
                  </div>
                ) : (
                  <Text fontStyle="BM" className={`mb-1 ${mainTextColor}`}>
                    {`${fillRatePoints} / ${progression?.duration} ${word("global.day.label", { count: program.sessionCount })}`}
                  </Text>
                )}
                <Circles
                  count={10}
                  filled={fillRatePoints}
                  progressionstyle="circle"
                  color="currentColor"
                  className={mainTextColor}
                />
              </div>
              {progression?.startedAt && (
                <div className="flex w-1/2 flex-col items-center justify-start">
                  <Text fontStyle="LS" className={secondaryTextColor}>
                    {word("program.details.progression.start_date")}
                  </Text>
                  <Text fontStyle="BM" className={mainTextColor}>
                    {getFormatedDateTime(progression.startedAt)}
                  </Text>
                </div>
              )}
            </div>

            {(isNotStarted || isPaused) && editionAllowed && (
              <Button variant="primary" onClick={() => void handleStartProgram()} disabled={isUpdating}>
                <PlayIcon className={mainTextColor} color="currentColor" />
                {word(isPaused ? "program.button.resume" : "program.button.start")}
              </Button>
            )}

            {isPaused && progression.stoppedAt && editionAllowed && (
              <Text fontStyle="BS" className="m-auto mt-2">
                {word("program.details.progression.resume", { date: getFormatedDateTime(progression.stoppedAt) })}
              </Text>
            )}

            {program.status === ProgramStatus.PUBLISHED && isStarted && editionAllowed && (
              <Button variant="primary" onClick={() => void handleStopProgram()} disabled={isUpdating}>
                <PauseIcon className={mainTextColor} color="currentColor" />
                {word("program.button.pause")}
              </Button>
            )}

            {(isPaused || isCompleted || isStarted) &&
            progression.currentIndex &&
            progression.currentIndex > 0 &&
            editionAllowed ? (
              <Button variant="outline" size="lg" onClick={handleRestartProgram} disabled={isUpdating}>
                <ResumeIcon color="currentColor" className="text-textDefault-heading" />
                {word("program.button.restart")}
              </Button>
            ) : null}
          </>
        ) : (
          <div>
            <Text fontStyle="BM">{word("program.onOff.progressionRate.description")}</Text>
          </div>
        )}
      </div>
    </div>
  )
}
